/// Help-card content. Each card explains one game concept in
/// 2–3 sentences for new players. Surfaced via the `?` button on
/// the home + game screens (HelpModal → Concepts tab) and as the
/// final page of the WelcomeModal's "next steps" link.
///
/// Order is the order they render in the modal — keep the most
/// commonly-needed concepts at the top.

export interface ConceptCard {
  id: string
  title: string
  /// Short body, plain text. Markdown not supported — keep
  /// punctuation simple.
  body: string
}

export const CONCEPT_CARDS: readonly ConceptCard[] = [
  {
    id: 'select',
    title: 'Selecting an aircraft',
    body:
      'Click or tap any aircraft on the radar to select it. The command panel opens with its current heading, altitude, and speed plus controls for issuing instructions.',
  },
  {
    id: 'heading-alt-speed',
    title: 'Heading, altitude, speed',
    body:
      'Type a value and hit Enter. Headings are 0–359° (compass). Altitudes round to the nearest 100 ft. Speed is clamped to the aircraft type — pushing past minimum or maximum returns "Unable" and snaps to the limit.',
  },
  {
    id: 'cleared-approach',
    title: 'Cleared for approach',
    body:
      'When an aircraft is within ~15 NM of an active runway and roughly aligned with it, the "Cleared for approach" button engages a localizer-intercept autopilot. The aircraft turns onto the extended centerline; you keep altitude under your control until you clear it to land.',
  },
  {
    id: 'cleared-land',
    title: 'Cleared to land',
    body:
      'Final clearance. Available once the aircraft is on the localizer, within range, and at a sensible descent rate. The autopilot flies the threshold and touches down. If conditions go bad mid-approach, use Go Around to climb out and try again.',
  },
  {
    id: 'direct-to',
    title: 'Direct to a fix',
    body:
      'Sends the aircraft straight to a named waypoint, ignoring its previous routing. Useful for shortcutting a transition aircraft to its exit fix or repositioning an arrival before the approach.',
  },
  {
    id: 'hold',
    title: 'Hold at a fix',
    body:
      'A right-hand racetrack pattern at the named fix. Used to delay an aircraft when the airport is busy or you need separation. Cancel-hold or a fresh direct-to ends the pattern.',
  },
  {
    id: 'time-multiplier',
    title: 'Time speed-up',
    body:
      'The 1× / 2× / 4× / 8× toggle in the toolbar speeds up the simulation. Useful for grinding through quiet stretches; everything (commands, descents, separation, scoring) scales proportionally.',
  },
  {
    id: 'goals',
    title: 'Goals & success target',
    body:
      'Each level has a mix of landings, transitions, and departures. Pass by handling the "need N" target shown beneath the score. Crashes, conflicts, and procedure busts cost points — clean flying earns a higher skill rating on the end screen.',
  },
] as const
